




















import { computed, defineComponent, ref } from "@vue/composition-api";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { InputAppointmentSlotBook } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentSlotProvider";
import {
  AppointmentType,
  AppointmentTypeList,
} from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import useFollowing from "@/hooks/useFollowing";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { blockPurchaseIfAgeNotVerified } from "@/lib/ageVerificationPurchaseGuard";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { NotificationData } from "@/types";
import { EVENTS, ERRORS } from "@/const";
import SlotNotAvailableConfirm from "./components/SlotNotAvailableConfirm";
import UserBookAppointmentModalView from "./components/UserBookAppointmentModalView";
import UserConfirmBookingModal from "./components/UserConfirmBookingModal";

export default defineComponent({
  name: "UserBookAppointmentsModal",
  components: { SlotNotAvailableConfirm, UserBookAppointmentModalView, UserConfirmBookingModal },
  props: {
    show: { type: Boolean, required: true },
    user: { type: Object as () => UserPublicData, required: true },
  },
  setup(props, { emit }) {
    const loading = ref(false);
    const { checkFollowingById, toggle } = useFollowing(ref([]));
    const { authUser } = useAuthUser();

    const paymentPackages = ref<PaymentPackage[]>([]);
    const pricePackageId = ref("");
    const purchaseProcessing = ref(false);
    const selectedStartTime = ref("");
    const isFollowing = ref(false);
    const appointmentTypes = ref<AppointmentTypeList | null>(null);
    const paymentSystemsPublic = ref<PaymentSystemPublic[]>([]);
    const selectedAppointmentTypeId = ref("");
    const subjectType = PurchaseSubjectTypeEnum.EVENT;
    const appointment = ref<Appointment | null>(null);
    const appointmentAuthor = computed(() => appointment.value && appointment.value.provider);
    const bookAppointmentSlot = async (data: InputAppointmentSlotBook): Promise<Appointment> => {
      return await providers.AppointmentSlotProvider.book({ data: data });
    };

    const slotNotification: NotificationData = {
      text: $t("Time slot unavailable 😔 Please choose another one to meet with me 🙏"),
      image: {
        user: props.user,
      },
      timeout: 2000,
      button: {
        text: $t("Ok"),
      },
    };

    const onChooseSystem = async () => {
      if (blockPurchaseIfAgeNotVerified(authUser.value)) {
        return;
      }
      purchaseProcessing.value = true;
      try {
        providers.ContentPriceProvider.setReceiveErrors(false);
        appointment.value = await bookAppointmentSlot({
          typeId: selectedAppointmentTypeId.value,
          startTime: new Date(selectedStartTime.value).toISOString(),
        });
        emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, {
          text: $t("Your purchase was successful"),
          button: { text: $t("Close") },
        });
        !isFollowing.value && (await toggle(props.user.id));
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === ERRORS.SPENDING_BALANCE,
            onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
          },
          {
            comparator: e => e.details?.messageTemplate === ERRORS.SLOT_IS_NOT_AVAILABLE,
            onSuccessCB: () => {
              emitter.emit(EVENTS.NOTIFICATION_SHOW, slotNotification);
            },
          },
        ]);
      } finally {
        purchaseProcessing.value = false;
      }
    };

    const appointmentBookingConfirm = async (
      { id: appointmentTypeId, pricePackageId: packageId }: AppointmentType,
      selectStartTime: string
    ) => {
      pricePackageId.value = packageId;
      selectedStartTime.value = selectStartTime;
      selectedAppointmentTypeId.value = appointmentTypeId;
      await onChooseSystem();
    };

    checkFollowingById(props.user.id).then(res => (isFollowing.value = res));

    const getPaymentPackages = async () => {
      paymentPackages.value = await providers.PaymentPackageProvider.findAll({
        type: [PaymentPackageEnum.APPOINTMENT],
      });
    };

    const getPaymentSystems = async () => {
      paymentSystemsPublic.value = await providers.PaymentSystemsProvider.findAllPublic();
    };

    const getAppointmentTypes = async () => {
      const res = await providers.AppointmentTypeProvider.search({
        data: { userIds: [props.user.id] },
      });
      appointmentTypes.value = res.find(item => item.userId === props.user.id) ?? null;
      if (!appointmentTypes.value) {
        emit("close");
      }
    };

    const init = async () => {
      try {
        loading.value = true;
        await Promise.all([getPaymentPackages(), getPaymentSystems(), getAppointmentTypes()]);
      } finally {
        loading.value = false;
      }
    };
    init();
    return {
      loading,
      appointment,
      subjectType,
      isFollowing,
      pricePackageId,
      paymentPackages,
      appointmentAuthor,
      selectedStartTime,
      purchaseProcessing,
      paymentSystemsPublic,
      selectedAppointmentTypeId,
      appointmentTypes,
      onChooseSystem,
      appointmentBookingConfirm,
    };
  },
});
